import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Radar, ScrollText, BarChart3, Star, LogOut, User, Zap, CalendarDays, Activity, Crown, KeyRound, Settings, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/scanner", label: "Scanner", icon: Radar, testid: "nav-scanner" },
  { to: "/catalysts", label: "Catalysts", icon: CalendarDays, testid: "nav-catalysts" },
  { to: "/journal", label: "Journal", icon: ScrollText, testid: "nav-journal" },
  { to: "/stats", label: "Stats", icon: Activity, testid: "nav-stats" },
  { to: "/portfolio", label: "Portfolio", icon: BarChart3, testid: "nav-portfolio" },
  { to: "/watchlist", label: "Watchlist", icon: Star, testid: "nav-watchlist" },
];

export default function Navbar() {
  const { user, logout, openAuth } = useAuth();
  const { plan, pro, openPortal } = usePlan();
  const loc = useLocation();
  const nav = useNavigate();
  const active = (to) => loc.pathname === to || (to === "/scanner" && loc.pathname === "/");

  return (
    <header className="sticky top-0 z-50 glass border-b border-[#222F43]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-14 md:h-16 flex items-center justify-between gap-4">
        <Link to="/scanner" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center shadow-[0_0_18px_rgba(0,255,102,0.5)]">
            <Zap className="h-5 w-5 text-black" strokeWidth={2.6} />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight">
            Catalyst<span className="text-emerald-400">Caddy</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active(n.to) ? "bg-emerald-500/12 text-emerald-400" : "text-slate-400 hover:text-slate-100 hover:bg-[#172030]"
                }`}>
                <Icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {plan && !pro && (
            <Link to="/pricing" data-testid="nav-upgrade"
              className="tap flex items-center gap-1.5 px-3 md:px-3.5 py-1.5 md:py-2 rounded-full text-xs font-display font-extrabold bg-emerald-500/12 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors">
              <Zap className="h-3.5 w-3.5" /> Go Pro
            </Link>
          )}
          {pro && (
            <Link to="/pricing" data-testid="nav-pro-badge" className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold tracking-widest bg-amber-400/10 text-amber-400 border border-amber-400/30">
              <Crown className="h-3 w-3" /> PRO
            </Link>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#334566] bg-transparent gap-2 h-9 w-9 p-0 md:h-10 md:w-auto md:px-4" data-testid="user-menu-btn">
                  <User className="h-4 w-4 text-emerald-400" />
                  <span className="hidden md:inline max-w-[110px] truncate" data-testid="nav-username">{user.username ? `@${user.username}` : user.name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass border-[#334566]">
                <DropdownMenuItem disabled className="text-xs text-slate-500 font-mono">{user.email}</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#334566]" />
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account">
                  <User className="h-4 w-4 mr-2" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/portfolio")} data-testid="menu-portfolio">
                  <BarChart3 className="h-4 w-4 mr-2" /> My Portfolio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/pricing")} data-testid="menu-plan">
                  <Crown className="h-4 w-4 mr-2" /> {pro ? "My plan" : "Upgrade to Pro"}
                </DropdownMenuItem>
                {plan?.subscription?.stripe_customer_id && (
                  <DropdownMenuItem onClick={openPortal} data-testid="menu-billing">
                    <Settings className="h-4 w-4 mr-2" /> Manage billing
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <>
                    <DropdownMenuItem onClick={() => nav("/admin/codes")} data-testid="menu-admin-codes">
                      <KeyRound className="h-4 w-4 mr-2" /> Lifetime codes
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/admin/team")} data-testid="menu-admin-team">
                      <ShieldCheck className="h-4 w-4 mr-2" /> Admins
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={logout} className="text-red-400" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={openAuth} className="tap bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-9 md:h-10" data-testid="signin-btn">
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
